import FFT from 'fft.js';
// Pitch conversion lives with the playback clock, so there is one definition of
// what a MIDI number sounds like rather than one per subsystem.
import { hzToMidi } from '../engine/timing';

/**
 * Signal analysis for microphone detection (plan doc 11).
 *
 * Everything here is a pure function over sample buffers, with no audio graph
 * and no DOM, for two reasons: the AudioWorklet and the ScriptProcessor
 * fallback run exactly the same code, and every one of these can be tested
 * against a synthesised signal whose answer is known.
 */

/* ----------------------------------------------------------------- window */

/**
 * A periodic Hann window. Periodic rather than symmetric, because these windows
 * are used for spectral analysis of overlapping frames, not for filter design.
 */
export function hannWindow(size: number): Float32Array {
  const w = new Float32Array(size);
  for (let i = 0; i < size; i += 1) {
    w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / size));
  }
  return w;
}

export function applyWindow(samples: Float32Array, window: Float32Array): Float32Array {
  const out = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) out[i] = (samples[i] ?? 0) * (window[i] ?? 0);
  return out;
}

/** Root mean square of a buffer, the level the noise gate works on. */
export function rms(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const s = samples[i] ?? 0;
    sum += s * s;
  }
  return Math.sqrt(sum / Math.max(1, samples.length));
}

/** Amplitude as decibels relative to full scale. Silence reports -Infinity. */
export function toDb(amplitude: number): number {
  return amplitude <= 0 ? -Infinity : 20 * Math.log10(amplitude);
}

/** True when the buffer hit the rails, which makes every other figure a lie. */
export function isClipping(samples: Float32Array, threshold = 0.99): boolean {
  for (let i = 0; i < samples.length; i += 1) {
    if (Math.abs(samples[i] ?? 0) >= threshold) return true;
  }
  return false;
}

/* --------------------------------------------------------------- spectrum */

/**
 * Magnitude spectra, reusing its buffers so the audio thread allocates nothing
 * per frame. `fftSize` must be a power of two.
 */
export class SpectrumAnalyzer {
  readonly fftSize: number;
  readonly binCount: number;
  private readonly fft: FFT;
  private readonly spectrum: number[];
  private readonly mags: Float32Array;

  constructor(fftSize: number) {
    if (fftSize < 4 || (fftSize & (fftSize - 1)) !== 0) {
      throw new Error(`fftSize must be a power of two of at least 4, got ${fftSize}`);
    }
    this.fftSize = fftSize;
    this.binCount = fftSize / 2;
    this.fft = new FFT(fftSize);
    this.spectrum = this.fft.createComplexArray() as number[];
    this.mags = new Float32Array(this.binCount);
  }

  /**
   * Magnitudes of the first half of the spectrum. The returned array is reused
   * on the next call; copy it to keep it.
   */
  magnitudes(windowed: Float32Array): Float32Array {
    const input = windowed.length === this.fftSize ? windowed : resizeTo(windowed, this.fftSize);
    this.fft.realTransform(this.spectrum, input as unknown as number[]);
    this.fft.completeSpectrum(this.spectrum);
    for (let i = 0; i < this.binCount; i += 1) {
      const re = this.spectrum[2 * i] ?? 0;
      const im = this.spectrum[2 * i + 1] ?? 0;
      this.mags[i] = Math.sqrt(re * re + im * im);
    }
    return this.mags;
  }
}

function resizeTo(samples: Float32Array, size: number): Float32Array {
  const out = new Float32Array(size);
  out.set(samples.subarray(0, Math.min(size, samples.length)));
  return out;
}

/** Hz at the center of a bin. */
export function binToHz(bin: number, sampleRate: number, fftSize: number): number {
  return (bin * sampleRate) / fftSize;
}

export function hzToBin(hz: number, sampleRate: number, fftSize: number): number {
  return (hz * fftSize) / sampleRate;
}

/**
 * Spectral flux: the rectified rise in magnitude since the previous frame. Only
 * increases count, because an onset is energy appearing, not energy leaving.
 */
export function spectralFlux(mags: Float32Array, previous: Float32Array | null): number {
  if (previous === null) return 0;
  let sum = 0;
  const n = Math.min(mags.length, previous.length);
  for (let i = 0; i < n; i += 1) {
    const d = (mags[i] ?? 0) - (previous[i] ?? 0);
    if (d > 0) sum += d;
  }
  return sum / Math.max(1, n);
}

/** The spectrum's center of mass in Hz, which rises on a down-strum. */
export function spectralCentroid(mags: Float32Array, sampleRate: number, fftSize: number): number {
  let weighted = 0;
  let total = 0;
  for (let i = 0; i < mags.length; i += 1) {
    const m = mags[i] ?? 0;
    weighted += m * binToHz(i, sampleRate, fftSize);
    total += m;
  }
  return total === 0 ? 0 : weighted / total;
}

/**
 * Spectral whitening: divide each bin by a running mean over a third of an
 * octave around it. This flattens the instrument's own timbre, so a chord
 * detector compares which pitches are present rather than how bright the
 * instrument happens to be.
 */
export function whiten(mags: Float32Array, sampleRate: number, fftSize: number): Float32Array {
  const out = new Float32Array(mags.length);
  const ratio = 2 ** (1 / 6); // a third of an octave, half either side
  for (let i = 0; i < mags.length; i += 1) {
    const hz = binToHz(i, sampleRate, fftSize);
    if (hz <= 0) continue;
    const lo = Math.max(0, Math.floor(hzToBin(hz / ratio, sampleRate, fftSize)));
    const hi = Math.min(mags.length - 1, Math.ceil(hzToBin(hz * ratio, sampleRate, fftSize)));
    let sum = 0;
    let count = 0;
    for (let j = lo; j <= hi; j += 1) {
      sum += mags[j] ?? 0;
      count += 1;
    }
    const mean = count > 0 ? sum / count : 0;
    out[i] = mean > 1e-12 ? (mags[i] ?? 0) / mean : 0;
  }
  return out;
}

/** Magnitude at a fractional bin, linearly interpolated between neighbors. */
function magnitudeAt(mags: Float32Array, bin: number): number {
  if (bin <= 0) return mags[0] ?? 0;
  const i = Math.floor(bin);
  if (i >= mags.length - 1) return mags[mags.length - 1] ?? 0;
  const frac = bin - i;
  const a = mags[i] ?? 0;
  const b = mags[i + 1] ?? 0;
  return a + frac * (b - a);
}

/**
 * Energy within `cents` of a frequency.
 *
 * The search is sampled across the interval with interpolation rather than by
 * widening to whole bins. Widening looks harmless but is not: at 4096 points
 * and 48 kHz a bin is 11.7 Hz, which at C4 is about 75 cents, so reaching one
 * bin past the interval picks up the neighboring semitone. A ukulele C then
 * reads energy on D and F as strongly as on its own strings, and every
 * correctly played chord is penalised for notes nobody played.
 *
 * Interpolating keeps the interval honest at any frequency, including the low
 * strings where +/-35 cents is narrower than a single bin.
 */
export function energyAroundHz(
  mags: Float32Array,
  sampleRate: number,
  fftSize: number,
  hz: number,
  cents = 35,
): number {
  const factor = 2 ** (cents / 1200);
  const from = hzToBin(hz / factor, sampleRate, fftSize);
  const to = hzToBin(hz * factor, sampleRate, fftSize);
  const steps = 8;
  let peak = 0;
  for (let i = 0; i <= steps; i += 1) {
    const bin = from + ((to - from) * i) / steps;
    peak = Math.max(peak, magnitudeAt(mags, bin));
  }
  return peak;
}

/**
 * The frequency of the strongest bin near `hz`, with parabolic interpolation, or
 * null when nothing is there. Used to spot a string fretted flat or sharp.
 */
export function peakNearHz(
  mags: Float32Array,
  sampleRate: number,
  fftSize: number,
  hz: number,
  cents = 60,
): number | null {
  const factor = 2 ** (cents / 1200);
  const from = Math.max(1, Math.floor(hzToBin(hz / factor, sampleRate, fftSize)));
  const to = Math.min(mags.length - 2, Math.ceil(hzToBin(hz * factor, sampleRate, fftSize)));
  if (to < from) return null;

  let best = from;
  for (let i = from; i <= to; i += 1) {
    if ((mags[i] ?? 0) > (mags[best] ?? 0)) best = i;
  }
  if ((mags[best] ?? 0) <= 0) return null;

  // Parabolic interpolation over the peak and its neighbors.
  const a = mags[best - 1] ?? 0;
  const b = mags[best] ?? 0;
  const c = mags[best + 1] ?? 0;
  const denom = a - 2 * b + c;
  const shift = denom === 0 ? 0 : (0.5 * (a - c)) / denom;
  return binToHz(best + shift, sampleRate, fftSize);
}

/* ----------------------------------------------------------------- chroma */

/**
 * Harmonic pitch class profile: each bin contributes to its own pitch class and,
 * with decreasing weight, to the pitch classes of its sub-harmonics, so an
 * overtone series folds back onto the note that produced it.
 */
export function chroma(
  mags: Float32Array,
  sampleRate: number,
  fftSize: number,
  options: { minHz?: number; maxHz?: number; a4Hz?: number } = {},
): Float32Array {
  // The upper bound is deliberately low, and 950 Hz is measured rather than
  // guessed. A triad's third contributes a third harmonic a twelfth above
  // itself — E4's lands on B5 at 989 Hz — and once that is in range a plain
  // major triad scores higher against the major-seventh template than against
  // the major one. Below 950 a C major triad matches `maj` with a margin of
  // 0.09; at 1100 it matches `maj7` instead.
  //
  // The cost is that a voicing whose fundamentals all sit above 950 Hz is
  // invisible here. That is rare in the chords this is used for, and the
  // voicing-aware detector handles fretted instruments, but it is the first
  // thing to re-check against real recordings.
  const { minHz = 60, maxHz = 950, a4Hz = 440 } = options;
  const out = new Float32Array(12);
  const weights = [1, 0.5, 1 / 3, 0.25];

  for (let i = 1; i < mags.length; i += 1) {
    const hz = binToHz(i, sampleRate, fftSize);
    if (hz < minHz || hz > maxHz) continue;
    const m = mags[i] ?? 0;
    if (m <= 0) continue;
    for (let h = 0; h < weights.length; h += 1) {
      const f = hz / (h + 1);
      if (f < minHz / 2) break;
      const midi = hzToMidi(f, a4Hz);
      if (!Number.isFinite(midi)) continue;
      const pc = ((Math.round(midi) % 12) + 12) % 12;
      out[pc] = (out[pc] ?? 0) + m * (weights[h] ?? 0);
    }
  }
  return normalize(out);
}

function normalize(v: Float32Array): Float32Array {
  let sum = 0;
  for (let i = 0; i < v.length; i += 1) sum += (v[i] ?? 0) ** 2;
  const norm = Math.sqrt(sum);
  if (norm <= 1e-12) return v;
  for (let i = 0; i < v.length; i += 1) v[i] = (v[i] ?? 0) / norm;
  return v;
}

/** Chord qualities the chroma matcher knows, as semitones above the root. */
export const CHROMA_QUALITIES = {
  maj: [0, 4, 7],
  min: [0, 3, 7],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
  '7': [0, 4, 7, 10],
  maj7: [0, 4, 7, 11],
  m7: [0, 3, 7, 10],
  m7b5: [0, 3, 6, 10],
  dim7: [0, 3, 6, 9],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  '5': [0, 7],
} as const satisfies Record<string, readonly number[]>;

export type ChromaQuality = keyof typeof CHROMA_QUALITIES;

/** Degree weights: the root and third identify a chord more than its fifth. */
function degreeWeight(semitone: number): number {
  if (semitone === 0) return 1;
  if (semitone === 3 || semitone === 4) return 0.9;
  if (semitone === 7 || semitone === 6 || semitone === 8) return 0.8;
  return 0.85;
}

function buildTemplates(): { root: number; quality: ChromaQuality; vector: Float32Array }[] {
  const out: { root: number; quality: ChromaQuality; vector: Float32Array }[] = [];
  for (const quality of Object.keys(CHROMA_QUALITIES) as ChromaQuality[]) {
    const intervals = CHROMA_QUALITIES[quality];
    for (let root = 0; root < 12; root += 1) {
      const vector = new Float32Array(12);
      for (const semitone of intervals) {
        vector[(root + semitone) % 12] = degreeWeight(semitone);
      }
      out.push({ root, quality, vector: normalize(vector) });
    }
  }
  return out;
}

/** Built once: 12 roots × 12 qualities is small and never changes. */
export const CHROMA_TEMPLATES = buildTemplates();

export interface ChromaMatch {
  root: number;
  quality: ChromaQuality;
  score: number;
  /** How far ahead of the runner-up, which is what makes a match trustworthy. */
  margin: number;
}

/** Cosine similarity of a chroma vector against every template, best first. */
export function matchChroma(vector: Float32Array): ChromaMatch | null {
  let best: { root: number; quality: ChromaQuality; score: number } | null = null;
  let second = -Infinity;

  for (const template of CHROMA_TEMPLATES) {
    let dot = 0;
    for (let i = 0; i < 12; i += 1) dot += (vector[i] ?? 0) * (template.vector[i] ?? 0);
    if (best === null || dot > best.score) {
      if (best !== null) second = best.score;
      best = { root: template.root, quality: template.quality, score: dot };
    } else if (dot > second) {
      second = dot;
    }
  }

  if (best === null) return null;
  return { ...best, margin: best.score - (second === -Infinity ? 0 : second) };
}

/** Score for one specific chord, for "play this chord" rather than "name it". */
export function scoreChroma(vector: Float32Array, root: number, quality: ChromaQuality): number {
  const template = CHROMA_TEMPLATES.find((t) => t.root === root && t.quality === quality);
  if (template === undefined) return 0;
  let dot = 0;
  for (let i = 0; i < 12; i += 1) dot += (vector[i] ?? 0) * (template.vector[i] ?? 0);
  return dot;
}
